import { stringify } from "qs";
import { fetch, getHeaders, makeRequest } from "$utils/utils";
import config from "./config";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";

const { api_url: url, order_url } = config;

export enum ERRORS {
  EXISTS = "Cette expression de besoin existe déjà, veuillez modifier les informations ou annuler la création",
  ADD_ERROR = "Une erreur s'est produite lors de l'ajout de l'expression de besoin",
  UPDATE_ERROR = "Une erreur s'est produite lors de l'édition de l'expression de besoin",
  UPDATE_EDB_ERROR = "Une erreur s'est produite lors de l'édition de l'expression de besoin",
  EDB_ID_NOT_FOUND = "Id de l'EDB créée introuvable : impossible d'intégrer des commandes.",
  MISSING_FIELD = "Donnée manquante !",
}

export const getIntegratedOrdersByEdbId = async (idEdb: string) => {
  const headers = getHeaders("");
  headers.Authorization = "";
  try {
    const res = await fetch(
      `/edbs/edbId/${idEdb}`,
      {
        method: "GET",
        headers,
      },
      order_url
    );
    // Prisma API returns { data: edb }, unwrap it to match the expected format
    return res.data?.data || res.data;
  } catch (error: any) {
    return { error };
  }
};

export const updateIntegratedOrders = async (integratedOrders) => {
  const headers = getHeaders("");
  headers.Authorization = "";
  await fetch(
    `/edbs/${integratedOrders.id}`,
    {
      method: "PUT",
      headers,
      data: { data: integratedOrders },
    },
    order_url
  );
};

export const deleteIntegratedOrdersByEdbId = async (edbId) => {
  const headers = getHeaders("");
  headers.Authorization = "";
  await fetch(
    `/edbs/edbId/${edbId}`,
    {
      method: "DELETE",
      headers,
    },
    order_url
  );
};

export const AddEDB = async (token: string, data: any) => {
  try {
    const { integratedOrders } = data;
    const res = await makeRequest(
      `${url}/api/edb/create`,
      "POST",
      {
        ...data,
        nbIntegratedOrderProducts: integratedOrders?.orderProducts?.length || 0,
      },
      getHeaders(token)
    );
    return res;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("EDB already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

/**
 * If you don't provide the EDB's integratedOrders, they will be deleted.
 */
export const UpdateEDB = async (token: string, data: any, edbId: string) => {
  try {
    const { integratedOrders } = data;
    const res = await makeRequest(
      `${url}/api/edb/${edbId}`,
      "PUT",
      {
        ...data,
        nbIntegratedOrderProducts: integratedOrders?.orderProducts?.length || 0,
      },
      getHeaders(token)
    );
    return res;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("EDB already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

type UpdateEDBProductsData = {
  newProducts: any[];
  integratedOrders?: EDBIntegratedOrders;
};

/**
 * data.newProducts must contain only the new products (they are added to the existing ones)
 * data.integratedOrders must contain all the integratedOrders.orderProducts (the existing ones are overloaded)
 */
export const UpdateEDBProducts = async (
  token: string,
  data: UpdateEDBProductsData,
  edbId: string
) => {
  try {
    const { integratedOrders, newProducts } = data;
    const res = await makeRequest(
      `${url}/api/edb/${edbId}/products`,
      "PUT",
      {
        newProducts,
        integratedOrders,
        nbIntegratedOrderProducts:
          integratedOrders?.orderProducts?.length ?? undefined,
      },
      getHeaders(token)
    );
    return res;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error) {
      error = ERRORS.UPDATE_EDB_ERROR;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

// pagination
export const getEDBs = async (params) => {
  try {
    const { data } = await fetch(`/api/edb/withoutCache?${stringify(params)}`);
    return data;
  } catch (err) {
    throw new Error(err?.message);
  }
};

// Get EDBs with order products including product references
export const getEDBsByIds = async (params) => {
  try {
    let edbsIds;

    if (Array.isArray(params)) {
      edbsIds = params;
    } else if (params.$inId) {
      edbsIds = params.$inId;
    } else if (params.edbsIds) {
      edbsIds = params.edbsIds;
    } else {
      edbsIds = [params];
    }

    const { data } = await fetch(
      `/api/edb/edbsByIds?${stringify({ edbsIds })}`
    );
    return data;
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const checkProductExistInEdb = async (id) => {
  try {
    const { data } = await fetch(`/api/edb/productExist/${id}`);
    return data;
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const isEdbExistForProduct = async (ids) => {
  try {
    const { data } = await fetch(
      `/api/edb/isEdbExistForProduct?${stringify({
        productReceptionId: ids,
      })}`
    );
    return data;
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const getEdbById = async (id) => {
  try {
    const { data } = await fetch(`/api/edb/${id}`);

    return data.data;
  } catch (err) {
    return { error: err?.message || null };
  }
};

export const GetEDBs = async (token: string, idCompany?: string) => {
  try {
    const params = idCompany ? `?company=${idCompany}` : "";
    const res = await makeRequest(
      `${url}/api/edb${params}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("EDB already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

export const AddOP = async (token: string, op: any) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops/create`,
      "POST",
      op,
      getHeaders(token)
    );
    if (!res || !res.data) {
      throw new Error(ERRORS.EDB_ID_NOT_FOUND);
    }
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("OP already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

export const GetOpsByEdb = async (token: string, idEdb: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops?edb=${idEdb}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("OP already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

export const GetOpsByReceptionReferences = async (
  token: string,
  receptionReferences: number[]
) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops?receptionReferences=${receptionReferences}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("OP already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

export const getOpsByEdbAndReceptionReferences = async (
  token: string,
  edbId: string,
  receptionReferences: number[]
) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops?edb=${edbId}&receptionReferences=${receptionReferences}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("OP already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

export const GetOpsByCompany = async (token: string, idcompany: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops?company=${idcompany}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("OP already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

export const GetOps = async (token: string, edbId?: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops?${edbId ? `&edb=${edbId}` : ""}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data;
    if (error && error.includes("OP already exists")) {
      error = ERRORS.EXISTS;
    } else {
      error = (err as any)?.response?.data;
    }
    throw new Error(error);
  }
};

export const EditOP = async (token: string, data: any, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops/${id}`,
      "PUT",
      data,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when editing OP");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};

export const EditOPStatus = async (token: string, data: any, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops/${id}/status`,
      "PUT",
      data,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when editing OP");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};

export const DeleteOP = async (token: string, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops/${id}`,
      "DELETE",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when deleting OP");
    }
    return res.data.data;
  } catch (err: any) {
    throw new Error(err);
  }
};

export const EditOPItem = async (token: string, opItem: any, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/op-items/${id}`,
      "PUT",
      opItem,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when editing OPItem");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};

export const DeleteOPItem = async (token: string, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/op-items/${id}`,
      "DELETE",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when editing OPItem");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};

export const pushOP = async (token: string, body: any) => {
  const response = await makeRequest(
    `${url}/api/ops/sync`,
    "POST",
    body,
    getHeaders(token)
  );

  return response;
};

export const syncOP = async (token: string, body: any) => {
  const response = await makeRequest(
    `${url}/api/ops/sync/pull`,
    "POST",
    body,
    getHeaders(token)
  );

  return response;
};

/**
 * Delete a single EDB item
 */
export const DeleteEDBItem = async (
  token: string,
  edbId: string,
  edbItemId: string
) => {
  try {
    const res = await makeRequest(
      `${url}/api/edb/${edbId}/edb-item/${edbItemId}`,
      "DELETE",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when untracking edb integrated product");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};

/**
 * Delete all edb items related to idProduct (excluding the ones from a reception)
 */
export const DeleteEDBItemProduct = async (
  token: string,
  idEdb: string,
  idProduct: string
) => {
  try {
    const res = await makeRequest(
      `${url}/api/edb/${idEdb}/product/${idProduct}`,
      "DELETE",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when deleting EDB item");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};

export const DeleteEDB = async (token: string, edbId: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/edb/${edbId}`,
      "DELETE",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when editing OPItem");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};

export const cancelEDB = async (token: string, edbId: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/edb/${edbId}/cancel`,
      "PATCH",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when cancelling the EDB");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_EDB_ERROR);
  }
};

export const DuplicateEDB = async (token: string, edbId: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/edb/${edbId}/duplicate`,
      "POST",
      {},
      getHeaders(token)
    );
    return res.data?.data ?? res.data;
  } catch (err: any) {
    throw new Error(err?.response?.data || ERRORS.UPDATE_EDB_ERROR);
  }
};

export const SubstituteProductEdb = async (
  token: string,
  idEdb: string,
  idProduct: string,
  substituteBy: string
) => {
  try {
    const res = await makeRequest(
      `${url}/api/edb/${idEdb}/substitute-product`,
      "POST",
      { idProduct, substituteBy },
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when editing OPItem");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};

export const SubstitutionProductEdb = async (
  token: string,
  idEdb: string,
  productId: string,
  substitutionQty: number,
  substitutions: any
) => {
  try {
    const res = await makeRequest(
      `${url}/api/edb/${idEdb}/substitution`,
      "POST",
      { productId, substitutionQty, substitutions },
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when editing OPItem");
    }
    return res;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_EDB_ERROR);
  }
};

export const ExportPdf = async (token: string, idOp: string, op: object) => {
  try {
    const res = await makeRequest(
      `${url}/api/ops/${idOp}/pdf`,
      "POST",
      { op },
      getHeaders(token),
      "arraybuffer"
    );
    if (!res) {
      throw new Error("Error when generating pdf");
    }
    return res as any;
  } catch (err) {
    throw new Error(ERRORS.ADD_ERROR);
  }
};

/**
 * Fetches all OP data for an EDB in a single request through the gateway
 * Aggregates preparation, collisage, and livraison data
 */
export const GetAggregatedOpData = async (token: string, idEdb: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/operations?edb=${idEdb}`,
      "GET",
      undefined,
      getHeaders(token)
    );

    if (!res || !res.data) {
      throw new Error("Failed to fetch aggregated OP data");
    }

    return res.data;
  } catch (err) {
    const error = (err as any)?.response?.data;
    throw new Error(error || "Failed to fetch OP data from gateway");
  }
};

/**
 * Check which suborders are linked to EDBs
 */
export const checkSuborderEdbs = async (
  suborderIds: number[],
  token: string
) => {
  try {
    const res = await makeRequest(
      `${url}/api/order/edbs/checkSuborderEdbs`,
      "POST",
      { suborderIds },
      getHeaders(token)
    );

    if (!res || !res.data) {
      return { subordersWithEdbs: [] };
    }

    return res.data;
  } catch (err) {
    console.error("[checkSuborderEdbs] Error", err);
    return { subordersWithEdbs: [] };
  }
};

export const getEdbApprovalInfo = async (edbId: string) => {
  const { data } = await fetch(`/api/edb/${edbId}/approval`);
  return data?.data as { canApprove: boolean; edb: any };
};

export const approveEdb = async (
  token: string,
  edbId: string,
  comment?: string
) => {
  const res = await makeRequest(
    `${url}/api/edb/${edbId}/approve`,
    "POST",
    { comment: comment || null },
    getHeaders(token)
  );
  return res?.data;
};

export const refuseEdb = async (
  token: string,
  edbId: string,
  comment?: string
) => {
  const res = await makeRequest(
    `${url}/api/edb/${edbId}/refuse`,
    "POST",
    { comment: comment || null },
    getHeaders(token)
  );
  return res?.data;
};
